import {
  Children,
  Fragment,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

type Option = { value: string; label: string; disabled: boolean };
type OptionElement = ReactElement<{ value?: string | number; disabled?: boolean; children?: ReactNode }>;

function nodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  return Children.toArray(node).map(nodeText).join("");
}

function readOptions(children: ReactNode): Option[] {
  return Children.toArray(children).flatMap((child): Option[] => {
    if (!isValidElement(child)) return [];
    if (child.type === Fragment) return readOptions((child as ReactElement<{ children?: ReactNode }>).props.children);
    if (child.type !== "option") return [];
    const option = child as OptionElement;
    const label = nodeText(option.props.children);
    return [{ value: String(option.props.value ?? label), label, disabled: Boolean(option.props.disabled) }];
  });
}

export function AdaptiveSelect({ children, className = "", value, defaultValue, onChange, disabled, multiple, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  const sourceRef = useRef<HTMLSelectElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [coarsePointer, setCoarsePointer] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [internalValue, setInternalValue] = useState(() => String(defaultValue ?? ""));
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280, above: false });
  const options = useMemo(() => readOptions(children), [children]);
  const selectedValue = value === undefined ? (internalValue || options[0]?.value || "") : String(value);
  const selected = options.find((option) => option.value === selectedValue) ?? options[0];
  const searchable = options.length >= 8;
  const visibleOptions = query
    ? options.filter((option) => option.label.toLocaleLowerCase("fr").includes(query.toLocaleLowerCase("fr")))
    : options;

  useEffect(() => {
    const media = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarsePointer(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(Math.max(rect.width, 240), 420, window.innerWidth - 24);
      const estimatedHeight = Math.min(options.length * 44 + (searchable ? 68 : 16), 356);
      const roomBelow = window.innerHeight - rect.bottom;
      const above = roomBelow < estimatedHeight + 16 && rect.top > roomBelow;
      setPosition({
        top: above ? Math.max(12, rect.top - estimatedHeight - 8) : Math.max(12, Math.min(window.innerHeight - estimatedHeight - 12, rect.bottom + 8)),
        left: Math.min(Math.max(12, rect.left), window.innerWidth - width - 12),
        width,
        above,
      });
    };
    updatePosition();
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) setOpen(false);
    };
    const reposition = () => updatePosition();
    document.addEventListener("pointerdown", closeOutside);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    if (searchable) requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, options.length, searchable]);

  const choose = (nextValue: string) => {
    const source = sourceRef.current;
    if (!source) return;
    const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value")?.set;
    setter?.call(source, nextValue);
    source.dispatchEvent(new Event("change", { bubbles: true }));
    if (value === undefined) setInternalValue(nextValue);
    setOpen(false);
    setQuery("");
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const moveFromTrigger = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;
    event.preventDefault();
    setOpen(true);
  };

  const handlePanelKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const buttons = Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>(".adaptive-select-option:not(:disabled)") ?? []);
    if (!buttons.length) return;
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? (current + 1) % buttons.length : (current <= 0 ? buttons.length - 1 : current - 1);
    buttons[next]?.focus();
  };

  const nativeSelect = (
    <select
      {...props}
      ref={sourceRef}
      className={coarsePointer || multiple ? `${className} adaptive-select-native`.trim() : "adaptive-select-source"}
      value={value}
      defaultValue={value === undefined ? defaultValue : undefined}
      onChange={(event) => {
        if (value === undefined) setInternalValue(event.target.value);
        onChange?.(event);
      }}
      disabled={disabled}
      multiple={multiple}
    >
      {children}
    </select>
  );

  if (coarsePointer || multiple) return nativeSelect;

  return <div className={`adaptive-select ${className}`.trim()}>
    {nativeSelect}
    <button
      ref={triggerRef}
      type="button"
      className="adaptive-select-trigger"
      aria-controls={`${id}-listbox`}
      aria-expanded={open}
      aria-haspopup="listbox"
      disabled={disabled}
      onClick={() => setOpen((current) => !current)}
      onKeyDown={moveFromTrigger}
    >
      <span>{selected?.label || "Choisir…"}</span>
      <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5.5 7.5 4.5 4.5 4.5-4.5" /></svg>
    </button>
    {open && createPortal(
      <div
        ref={panelRef}
        id={`${id}-listbox`}
        className={`adaptive-select-panel${position.above ? " opens-above" : ""}`}
        role="listbox"
        aria-label={props["aria-label"]}
        style={{ top: position.top, left: position.left, width: position.width }}
        onKeyDown={handlePanelKeys}
      >
        {searchable && <div className="adaptive-select-search"><svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.25" /><path d="m12.5 12.5 4 4" /></svg><input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher…" aria-label="Rechercher dans la liste" />{query && <button type="button" className="adaptive-select-search-clear" aria-label="Effacer la recherche" title="Effacer la recherche" onMouseDown={(event) => event.preventDefault()} onClick={() => { setQuery(""); window.requestAnimationFrame(() => searchRef.current?.focus()); }}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 6 8 8m0-8-8 8" /></svg></button>}</div>}
        <div className="adaptive-select-options">
          {visibleOptions.map((option) => <button
            type="button"
            className="adaptive-select-option"
            role="option"
            aria-selected={option.value === selectedValue}
            disabled={option.disabled}
            key={option.value}
            onClick={() => choose(option.value)}
          ><span>{option.label}</span>{option.value === selectedValue && <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4.5 10.5 3.25 3.25 7.75-8" /></svg>}</button>)}
          {!visibleOptions.length && <p className="adaptive-select-empty">Aucun résultat</p>}
        </div>
      </div>,
      document.body,
    )}
  </div>;
}
